import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import {
  buildViewColumns,
  buildViewMenuOptions,
  sortViewItems,
  toViewListItem,
  type ViewItem,
  type ViewMenuHandlers,
} from '@bizzdesign/data-access-workspaces';
import { type SearchScope } from '@bizzdesign/sdk-search';
import { type Item } from '@bizzdesign/shared/models';
import { SearchResultsDisplayBase } from '../search-results-display.base';
import { searchResultsTabImports } from '../search-results-tab.imports';
import { type SearchTabEmptyStateLink } from '../search-tab-empty-state/search-tab-empty-state.component';
import {
  type DisplayOption,
  frameSortSettings,
  lastEditedOption,
  type SortOption,
} from '@bizzdesign/workspaces/utils';

@Component({
  selector: 'bd-view-search-results',
  imports: [...searchResultsTabImports],
  templateUrl: './view-search-results.component.html',
  styleUrl: '../search-results-tabs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewSearchResultsComponent extends SearchResultsDisplayBase {
  viewItems = input<ViewItem[]>([]);
  loading = input<boolean>(false);
  totalCount = input<number>(0);
  currentPageIndex = input<number>(0);
  pageSize = input<number>(20);
  filtersHeight = input<number>(0);

  openView = output<ViewItem>();
  openViewDetails = output<ViewItem>();
  copyViewToFrame = output<ViewItem>();
  copyViewLink = output<ViewItem>();
  deleteView = output<ViewItem>();
  switchScope = output<SearchScope>();
  pageChange = output<number>();

  emptyStateLinks = input<SearchTabEmptyStateLink[]>([]);

  readonly sortSettings = frameSortSettings;
  readonly displayOption = signal<DisplayOption>('grid');
  readonly sortOption = signal<SortOption>(lastEditedOption.key);
  private readonly menuHandlers: ViewMenuHandlers = {
    onPreview: (viewItem) => this.openView.emit(viewItem),
    onSeeDetails: (viewItem) => this.openViewDetails.emit(viewItem),
    onCopyToFrame: (viewItem) => this.copyViewToFrame.emit(viewItem),
    onCopyLink: (viewItem) => this.copyViewLink.emit(viewItem),
    onDelete: (viewItem) => this.deleteView.emit(viewItem),
  };

  readonly sortedViewItems = computed((): ViewItem[] =>
    sortViewItems(this.viewItems(), this.sortOption()),
  );

  readonly listItems = computed<Item<ViewItem>[]>(() =>
    this.sortedViewItems().map((viewItem) =>
      toViewListItem(viewItem, {
        actionButton: {
          labelKey: 'workspaces.openView',
          tScope: 'featureWorkspaces',
          styleClass: 'bd-size-small',
          group: 'neutral',
          type: 'asLink',
          onClick: () => this.openView.emit(viewItem),
        },
        onSelect: () => this.openViewDetails.emit(viewItem),
        menuOptions: buildViewMenuOptions(viewItem, this.menuHandlers, {
          includeSeeDetails: true,
        }),
      }),
    ),
  );

  readonly viewColumns = buildViewColumns({ includeMenu: true });

  onSelectedItemsChange(
    item: Item<ViewItem> | Item<ViewItem>[] | undefined,
  ): void {
    if (item && !Array.isArray(item) && item.data) {
      this.openViewDetails.emit(item.data);
    }
  }

  onItemDoubleClick(item: Item<ViewItem>): void {
    if (item.data) {
      this.openView.emit(item.data);
    }
  }

  onPageChange(pageNumber: number): void {
    this.pageChange.emit(pageNumber);
  }
}
